import React, { useState } from "react";
import {
  Button,
  Chip,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getPOGrandTotal } from "../../purchase-order/helper";
import ShipToCell, {
  toShipToExcelValue,
  toShipToList,
} from "../common-components/ship-to-cell";

export const PR_STATUSES = [
  { label: "Draft", value: "created" },
  { label: "Draft (Edited)", value: "Draft" },
  { label: "Submitted", value: "approval_in_progress" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "Reconsider", value: "reconsider" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Converted to PO", value: "converted" },
  { label: "Short Closed", value: "short_close" },
];

export const PR_TYPES = [
  { label: "CAPEX", value: "CAPEX" },
  { label: "OPEX", value: "OPEX" },
];

export const STATUS_CHIP_PROPS = {
  created: { label: "Draft", color: "default" },
  Draft: { label: "Draft (Edited)", color: "default" },
  approval_in_progress: { label: "Approval in Progress", color: "warning" },
  approved: { label: "Approved", color: "success" },
  rejected: { label: "Rejected", color: "error" },
  reconsider: { label: "Reconsider", color: "warning" },
  cancelled: { label: "Cancelled", color: "default" },
  converted: { label: "Converted to PO", color: "info" },
  short_close: { label: "Short Closed", color: "info" },
};

const formatCurrency = (val) =>
  val != null
    ? `₹${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "-";

const TotalEstAmountCell = ({ row }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const linkedPOs = row.purchaseOrderIds || [];

  return (
    <>
      <Button
        variant="text"
        size="small"
        sx={{ textTransform: "none", fontWeight: 600, padding: 0, minWidth: 0 }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {formatCurrency(row.balanceEstimatedAmount)}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div style={{ width: 380, padding: 12 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <Typography variant="subtitle2">Amount Breakdown</Typography>
            <IconButton size="small" onClick={() => setAnchorEl(null)}>
              <Close fontSize="small" />
            </IconButton>
          </div>

          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>PR Total Est. Amount</TableCell>
                <TableCell align="right">
                  {formatCurrency(row.totalEstimatedGrossAmount)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {linkedPOs.length > 0 && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                style={{ display: "block", marginTop: 12, marginBottom: 4 }}
              >
                Purchase Order{linkedPOs.length > 1 ? "s" : ""} created against this PR
              </Typography>
              <TableContainer style={{ maxHeight: 240, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linkedPOs.map((po) => (
                      <TableRow key={po._id || po.id}>
                        <TableCell>{po.poNumber || po._id || po.id}</TableCell>
                        <TableCell>{po.status || "-"}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getPOGrandTotal(po))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </div>
      </Popover>
    </>
  );
};

const getLocationLabel = (code, name) =>
  name ? `${code ? code + " - " : ""}${name}` : code || null;

export const getShipToLabels = (row, storeList = []) => {
  if (
    row?.prScope === "SINGLE_STORE" ||
    !row?.storeSelectionType ||
    row?.storeSelectionType === "SELF"
  ) {
    const site = row?.site && typeof row.site === "object" ? row.site : {};
    return toShipToList([
      getLocationLabel(
        row?.siteCode || site.code,
        row?.siteName || site.name
      ),
    ]);
  }
  if (row?.storeSelectionType === "ALL") {
    return ["All Stores"];
  }
  return toShipToList(
    (row?.selectedSites || []).map((site) => {
      const siteId = site?._id ?? site?.id ?? site;
      const location =
        site && typeof site === "object" && site.name
          ? site
          : storeList.find(
              (store) => String(store?._id ?? store?.id) === String(siteId)
            );
      return location ? getLocationLabel(location.code, location.name) : null;
    })
  );
};

export const getShipToLabel = (row, storeList = []) =>
  toShipToExcelValue(getShipToLabels(row, storeList));

export const getPRHeadersConfig = (navigate, storeList = []) => {
  return [
    {
      label: "PR Number",
      id: "prNumber",
      render: (row) => (
        <Button
          variant="text"
          size="small"
          sx={{ textTransform: "none", fontWeight: 600 }}
          onClick={() => navigate(`purchase-requisition/${row.id || row._id}`)}
        >
          {row.prNumber || row.id}
        </Button>
      ),
    },
    {
      label: "PR Date",
      id: "prDate",
      render: (row) =>
        row.prDate ? new Date(row.prDate).toLocaleDateString("en-IN") : "-",
    },
    {
      label: "Store",
      id: "siteName",
      render: (row) =>
        row.siteName
          ? `${row.siteCode ? row.siteCode + " - " : ""}${row.siteName}`
          : "-",
    },
    {
      label: "Ship to",
      id: "shipTo",
      render: (row) => (
        <ShipToCell
          addresses={getShipToLabels(row, storeList)}
          prNumber={row.prNumber}
        />
      ),
    },
    {
      label: "PR Type",
      id: "prType",
      render: (row) => row.prType || "-",
    },
    {
      label: "Created By",
      id: "creatorName",
      render: (row) => row.creatorName || "-",
    },
    {
      label: "Created At",
      id: "createdAt",
      render: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-IN") : "-",
    },
    {
      label: "Total Est. Amount",
      id: "balanceEstimatedAmount",
      render: (row) => <TotalEstAmountCell row={row} />,
    },
    {
      label: "Status",
      id: "status",
      render: (row) => {
        const props = STATUS_CHIP_PROPS[row.status] || {
          label: row.status,
          color: "default",
        };
        return <Chip label={props.label} color={props.color} size="small" />;
      },
    },
    {
      label: "Actions",
      id: "actions",
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate(`purchase-requisition/${row.id || row._id}`)}
        >
          View
        </Button>
      ),
    },
  ];
};
